import {useParams, useNavigate} from "react-router-dom";
import {Tabs} from "antd";
import className from "classnames/bind";
import {Button, ExportButtons, FontAwesomeIcon, Loading, PageHeader, ProjectToolNav} from "~/components";
import {useGetTechnicalReportQuery} from "~/reduxs/api/technicalApiSlice";
import TechSummary from "../components/TechSummary";
import TechInternalLinks from "../components/TechInternalLinks";
import TechBrokenLinks from "../components/TechBrokenLinks";
import TechRedirects from "../components/TechRedirects";
import TechRobots from "../components/TechRobots";
import TechSitemap from "../components/TechSitemap";
import TechLlms from "../components/TechLlms";
import TechHtmlSitemap from "../components/TechHtmlSitemap";
import style from "../style/Technical.module.scss";

const cn = className.bind(style);

/** Dựng dữ liệu xuất Excel/PDF cho báo cáo kỹ thuật (Excel đầy đủ, PDF cắt 100 dòng/bảng). */
function buildTechExport(data) {
	const {summary = {}, internal = {}, broken = {}, redirects = [], robots = {}, crawl, project} = data;

	const summaryRows = [
		['Trang đã crawl', summary.pages], ['Link nội bộ', summary.internal_links],
		['Link ngoài', summary.external_links], ['Link nofollow', summary.nofollow_links],
		['Link nội bộ hỏng', summary.broken_internal], ['Link ngoài hỏng', summary.broken_external],
		['Lỗi server (5xx)', summary.server_errors], ['Chuyển hướng', summary.redirects],
		['Chuỗi redirect', summary.redirect_chains], ['Trang mồ côi', summary.orphans],
		['Trang bị chặn', summary.blocked],
	];

	const depthDist = internal.depth_dist || {};
	const depthRows = Object.keys(depthDist).map(Number).sort((a, b) => a - b)
		.map((d) => [d === 0 ? 'Trang gốc' : `Độ sâu ${d}`, depthDist[d]]);
	const topLinkedRows = (internal.top_linked || []).map((r) => [r.url, r.inlinks, r.status]);
	const oppRows = (internal.opportunities || []).map((r) => [r.url, r.inlinks, r.orphan ? 'Mồ côi' : '']);

	const brokenInternalRows = (broken.internal || []).map((r) => [r.url, r.status, r.sources]);
	const brokenExternalRows = (broken.external || []).map((r) => [r.url || r.message, r.status || 'Không kết nối', r.sources]);
	const serverRows = (broken.server || []).map((r) => [r.url, r.status]);

	const redirectRows = (redirects || []).map((r) => [r.url, r.to, r.hops, r.chain ? 'Chuỗi' : '']);

	const robotsRows = [
		['robots.txt', robots.found ? 'Tìm thấy' : 'Không tìm thấy'],
		['Crawl-delay', robots.crawl_delay ? `${robots.crawl_delay}s` : '—'],
		...(robots.sitemaps || []).map((s, i) => [i === 0 ? 'Sitemap khai báo' : '', s]),
	];
	const ruleRows = (robots.rules || []).map((r) => [r.type === 'allow' ? 'Allow' : 'Disallow', r.path]);
	const blockedRows = (robots.blocked || []).map((r) => [r.url || r.message]);

	const buildExcel = () => [
		{name: 'Tổng quan', aoa: [['Chỉ số', 'Giá trị'], ...summaryRows]},
		{name: 'Liên kết nội bộ', aoa: [
			['Phân bố độ sâu', 'Số trang'], ...depthRows,
			[], ['Top trang được liên kết', 'Link trỏ tới', 'HTTP'], ...topLinkedRows,
			[], ['Cơ hội liên kết (mồ côi - ít inlink)', 'Số link nội bộ', 'Ghi chú'], ...oppRows,
		]},
		{name: 'Liên kết hỏng', aoa: [
			['Link nội bộ hỏng', 'HTTP', 'Số trang trỏ tới'], ...brokenInternalRows,
			[], ['Link ngoài hỏng', 'HTTP', 'Số trang chứa link'], ...brokenExternalRows,
			[], ['Lỗi server 5xx', 'HTTP'], ...serverRows,
		]},
		{name: 'Chuyển hướng', aoa: [['Trang', 'Chuyển hướng tới', 'Số bước', 'Ghi chú'], ...redirectRows]},
		{name: 'Robots', aoa: [
			['Thông tin', 'Giá trị'], ...robotsRows,
			[], ['Luật', 'Đường dẫn'], ...ruleRows,
			[], ['URL bị chặn khi crawl'], ...blockedRows,
		]},
	];

	const cap = (rows) => rows.slice(0, 100);
	const capLabel = (rows) => `${Math.min(rows.length, 100)}/${rows.length}`;
	const buildPdf = () => ({
		title: 'Báo cáo Kỹ thuật SEO',
		subtitle: `${project?.root_url || ''} — crawl #${crawl.id}`,
		sections: [
			{heading: 'Tổng quan', columns: ['Chỉ số', 'Giá trị'], rows: summaryRows},
			{heading: `Link nội bộ hỏng (${capLabel(brokenInternalRows)})`, columns: ['URL đích (hỏng)', 'HTTP', 'Số trang trỏ tới'], rows: cap(brokenInternalRows)},
			{heading: `Link ngoài hỏng (${capLabel(brokenExternalRows)})`, columns: ['URL ngoài', 'HTTP', 'Số trang chứa link'], rows: cap(brokenExternalRows)},
			{heading: `Chuyển hướng (${capLabel(redirectRows)})`, columns: ['Trang', 'Chuyển hướng tới', 'Số bước', 'Ghi chú'], rows: cap(redirectRows)},
			{heading: `Cơ hội liên kết (${capLabel(oppRows)})`, columns: ['Trang', 'Số link nội bộ', 'Ghi chú'], rows: cap(oppRows)},
		],
	});

	return {filename: `ky-thuat-seo-crawl-${crawl.id}`, buildExcel, buildPdf};
}

/**
 * Trang tool "Kỹ thuật SEO" của một Project: đọc lần crawl mới nhất và trình bày theo tab
 * (Tổng quan / Liên kết nội bộ / Liên kết hỏng / Chuyển hướng / Robots.txt). Nếu project
 * chưa có lần crawl nào hoàn tất → gợi ý chạy Site Crawler trước.
 */
function TechnicalHome() {

	const {id} = useParams();
	const navigate = useNavigate();

	const {data, isLoading, isError} = useGetTechnicalReportQuery(id);

	if (isLoading) return <Loading />;

	const project = data?.project;

	const header = (
		<>
			<ProjectToolNav projectId={id} active="technical" />
			<PageHeader
				icon="fa-light fa-screwdriver-wrench"
				title="Kỹ thuật SEO"
				subtitle={project?.root_url}
			/>
		</>
	);

	if (isError || !data) {
		return <div className="container">{header}<div className={cn('empty')}>Không tải được dữ liệu.</div></div>;
	}

	if (!data.crawl) {
		return (
			<div className="container">
				{header}
				<div className={cn('empty')}>
					<FontAwesomeIcon icon="fa-light fa-spider" />
					<p>Dự án chưa có lần crawl nào hoàn tất. Hãy chạy Site Crawler để có dữ liệu kỹ thuật.</p>
					<Button primary onClick={() => navigate(`/projects/${id}/crawler`)}
						leftIcon={<FontAwesomeIcon icon="fa-light fa-play" />}>
						Mở Site Crawler
					</Button>
				</div>
			</div>
		);
	}

	const {summary, internal, broken, redirects, robots, crawl} = data;

	const items = [
		{key: 'summary', label: 'Tổng quan', children: <TechSummary summary={summary} />},
		{key: 'internal', label: `Liên kết nội bộ`, children: <TechInternalLinks internal={internal} />},
		{
			key: 'broken',
			label: <span>Liên kết hỏng {summary.broken_internal + summary.broken_external > 0 && <b className={cn('badge')}>{summary.broken_internal + summary.broken_external}</b>}</span>,
			children: <TechBrokenLinks broken={broken} />,
		},
		{key: 'redirects', label: 'Chuyển hướng', children: <TechRedirects projectId={id} redirects={redirects} />},
		{key: 'sitemap', label: 'Sitemap', children: <TechSitemap projectId={id} />},
		{key: 'html-sitemap', label: 'HTML sitemap', children: <TechHtmlSitemap projectId={id} />},
		{key: 'robots', label: 'Robots.txt', children: <TechRobots robots={robots} />},
		{key: 'llms', label: 'llms.txt', children: <TechLlms projectId={id} />},
	];

	return (
		<div className="container">
			{header}
			<div className={cn('crawlMeta')} style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, flexWrap: 'wrap'}}>
				<span>Dữ liệu từ lần crawl <strong>#{crawl.id}</strong> — {crawl.pages_crawled} trang — {crawl.finished_at || crawl.created}</span>
				<ExportButtons {...buildTechExport(data)} />
			</div>
			<Tabs defaultActiveKey="summary" items={items} />
		</div>
	);
}

export default TechnicalHome;
